'use client';
import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import { publicNav } from './publicData';
import { useCookieConsent } from '@/lib/consent/cookieConsent';
import { FALLBACK_FOOTER_LINKS, FooterLink, getCachedFooterLinks, sitePagesService } from '@/lib/sitePages/sitePagesService';
import { FooterFollowUs } from './SocialIcons';

export default function PublicFooter() {
  const { openBanner } = useCookieConsent();
  const [legalLinks, setLegalLinks] = useState<FooterLink[]>(() => {
    const cached = getCachedFooterLinks();
    return cached && cached.length > 0 ? cached : FALLBACK_FOOTER_LINKS;
  });
  const [year, setYear] = useState<number | null>(null);

  useEffect(() => {
    setYear(new Date().getFullYear());
    if (getCachedFooterLinks()) return;
    let cancelled = false;
    sitePagesService
      .getFooterLinks()
      .then((links) => {
        if (!cancelled && links.length > 0) setLegalLinks(links);
      })
      .catch(() => {
        // Keep fallback links
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <footer className="bg-sidebar text-primary-foreground">
      <div className="mx-auto flex w-full max-w-screen-xl flex-col gap-6 px-4 py-8 md:px-6 lg:px-8">
        <div className="flex flex-col gap-5 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-2.5">
            <AppLogo size={34} />
            <span className="text-sm font-bold tracking-tight">Care Business School</span>
          </div>
          <nav className="flex flex-wrap gap-x-5 gap-y-2 text-sm text-primary-foreground/75" aria-label="Footer navigation">
            {publicNav.map((item) => (
              <Link key={`f-${item.key}`} href={item.href} className="hover:text-accent">
                {item.label}
              </Link>
            ))}
            <Link href="/login" className="hover:text-accent">Member Login</Link>
            <Link href="/register" className="hover:text-accent">Register</Link>
            <Link href="/newsletter" className="hover:text-accent">Newsletter</Link>
          </nav>
        </div>
        <div className="gold-rule h-px w-full opacity-40" />
        <FooterFollowUs />
        <div aria-labelledby="footer-get-started">
          <h2 id="footer-get-started" className="mb-2 text-sm font-semibold text-primary-foreground">
            Get started
          </h2>
          <nav
            className="flex flex-wrap items-center gap-x-5 gap-y-1 text-sm text-primary-foreground/85"
            aria-label="Get started links"
          >
            <Link href="/webinar" className="inline-flex min-h-[32px] items-center hover:text-accent">
              Free Weekly Masterclass
            </Link>
            <Link href="/start" className="inline-flex min-h-[32px] items-center hover:text-accent">
              £49 Starter Course: Is a Care Business Right for You?
            </Link>
          </nav>
        </div>
        <nav
          className="flex flex-wrap items-center gap-x-5 gap-y-1 text-sm text-primary-foreground/85"
          aria-label="Legal and cookie links"
        >
          {legalLinks.map((l) => (
            <Link key={`legal-${l.slug}`} href={`/${l.slug}`} className="inline-flex min-h-[32px] items-center hover:text-accent">
              {l.label}
            </Link>
          ))}
          <button
            type="button"
            onClick={openBanner}
            className="inline-flex min-h-[32px] items-center hover:text-accent"
          >
            Cookie settings
          </button>
        </nav>
        <p className="text-xs leading-relaxed text-primary-foreground/60">
          © {year ?? ''} Care Business School Ltd · Company No. 17217121 · Registered in England and Wales · A subsidiary of Malevi Group Ltd
        </p>
      </div>
    </footer>
  );
}
